import React, { useMemo } from 'react';
import { Spinner } from '@/components/ui/Spinner';
import { SimpleTooltip } from '@/components/ui/Tooltip/Tooltip';
import { AirportAutocomplete } from '@/components/ui/Autocomplete/AirportAutocomplete';
import { FlightManifestService, type RecordWithSourceDTO } from '@/services/FlightManifestService';
import { Pencil, X, Check, Plus, Eye, Trash2 } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════

export interface FlightLeg {
	departureAirport: string | null;
	arrivalAirport: string | null;
	flightNumber: string | null;
	date: string | null;
	departureTime: string | null;
	arrivalTime: string | null;
	direction: 'outbound' | 'return';
	source: 'primary_outbound' | 'primary_return' | 'all_flights';
	allFlightsIndex?: number;
}

export interface BookingGroup {
	pnr: string;
	recordId: string;
	record: RecordWithSourceDTO;
	sourceFilename: string;
	fileId: string;
	legs: FlightLeg[];
}

export interface PassengerGroup {
	key: string;
	name: string;
	email: string | null;
	matchedAttendeeId: string | null;
	bookings: BookingGroup[];
	anomalyTypes: string[];
	hasFlag: boolean;
	isApproved: boolean;
	/** Extraction could not cleanly anchor the itinerary on the program destination — needs manual verification. */
	destinationMismatch: boolean;
}

export interface EditingCell {
	recordId: string;
	field: string;
	legSource?: 'primary_outbound' | 'primary_return' | 'all_flights';
	allFlightsIndex?: number;
}

export interface NewLegData {
	departure_airport: string;
	arrival_airport: string;
	flight_number: string;
	date: string;
	departure_time: string;
	arrival_time: string;
	direction: 'outbound' | 'return';
}

// ═══════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════

// Kept as stubs for backward compatibility — anomaly system removed
export const ANOMALY_TYPE_ICONS: Record<string, string> = {};
export const ANOMALY_TYPE_LABELS: Record<string, string> = {};
export const ANOMALY_TYPE_TINTS: Record<string, string> = {};

// ═══════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════

// Timezone-safe date parse. Dates from the manifest API are calendar dates
// ('YYYY-MM-DD' or ISO strings with T/Z that represent UTC midnight of that
// calendar day). `new Date(iso)` interprets them as UTC, so viewers west of UTC
// saw the previous day (the "date is off by one" bug). We always reconstruct the
// date in LOCAL time from the Y-M-D parts instead.
export const parseDate = (d: string): Date => {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
	if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	return new Date(d);
};

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;

export const formatDate = (d: string | null | undefined) => {
	if (!d) return '\u2014';
	const dt = parseDate(d);
	if (isNaN(dt.getTime())) return '\u2014';
	const dd = String(dt.getDate()).padStart(2, '0');
	const mmm = MONTH_ABBR[dt.getMonth()];
	return `${dd}/${mmm}/${dt.getFullYear()}`;
};

export const formatTime24 = (t: string | null | undefined) => {
	if (!t) return '\u2014';
	return t.slice(0, 5);
};

export function getRecordAnomalyTypes(_r: RecordWithSourceDTO): string[] {
	return [];
}

export function buildLegsFromRecord(r: RecordWithSourceDTO): FlightLeg[] {
	const allFlights = r.all_flights;
	if (allFlights && allFlights.length > 0) {
		return allFlights.map((f, i) => ({
			departureAirport: f.departure_airport || null,
			arrivalAirport: f.arrival_airport || null,
			flightNumber: f.flight_number || null,
			date: f.date || null,
			departureTime: f.departure_time || null,
			arrivalTime: f.arrival_time || null,
			direction: f.direction || 'outbound',
			source: 'all_flights' as const,
			allFlightsIndex: i,
		}));
	}
	const legs: FlightLeg[] = [];
	if (r.outbound_departure_airport || r.outbound_arrival_airport || r.outbound_departure_date) {
		legs.push({
			departureAirport: r.outbound_departure_airport,
			arrivalAirport: r.outbound_arrival_airport,
			flightNumber: r.outbound_flight_number,
			date: r.outbound_departure_date,
			departureTime: r.outbound_departure_time,
			arrivalTime: r.outbound_arrival_time,
			direction: 'outbound',
			source: 'primary_outbound',
		});
	}
	if (r.return_departure_airport || r.return_arrival_airport || r.return_flight_date) {
		legs.push({
			departureAirport: r.return_departure_airport,
			arrivalAirport: r.return_arrival_airport,
			flightNumber: r.return_flight_number,
			date: r.return_flight_date,
			departureTime: r.return_departure_time,
			arrivalTime: r.return_arrival_time,
			direction: 'return',
			source: 'primary_return',
		});
	}
	return legs;
}

export function groupRecordsByPassenger(records: RecordWithSourceDTO[]): PassengerGroup[] {
	const groupMap = new Map<string, PassengerGroup>();

	for (const r of records) {
		const key = r.matched_attendee_id
			|| (r.passenger_name ? r.passenger_name.toLowerCase().trim() : `__record_${r.id}`);

		if (!groupMap.has(key)) {
			groupMap.set(key, {
				key,
				name: r.passenger_name || `${r.passenger_first_name || ''} ${r.passenger_last_name || ''}`.trim() || '',
				email: r.passenger_email,
				matchedAttendeeId: r.matched_attendee_id,
				bookings: [],
				anomalyTypes: [],
				hasFlag: false,
				isApproved: true,
				destinationMismatch: false,
			});
		}

		const group = groupMap.get(key)!;
		const pnr = r.primary_pnr || r.client_id || `record-${r.id}`;
		const anomalyTypes = getRecordAnomalyTypes(r);
		const anomalyStatus = (r as any).anomaly_status;

		anomalyTypes.forEach(t => { if (!group.anomalyTypes.includes(t)) group.anomalyTypes.push(t); });
		if (anomalyStatus === 'flagged') group.hasFlag = true;
		if (r.extraction_flags?.destination_mismatch) group.destinationMismatch = true;
		if (!r.is_approved) group.isApproved = false;

		if (!group.name && r.passenger_name) group.name = r.passenger_name;
		if (!group.email && r.passenger_email) group.email = r.passenger_email;

		let booking = group.bookings.find(b => b.pnr === pnr);
		if (booking) {
			const newLegs = buildLegsFromRecord(r);
			for (const leg of newLegs) {
				const exists = booking.legs.some(
					l => l.departureAirport === leg.departureAirport
						&& l.arrivalAirport === leg.arrivalAirport
						&& l.date === leg.date
						&& l.direction === leg.direction
				);
				if (!exists) booking.legs.push(leg);
			}
		} else {
			group.bookings.push({
				pnr,
				recordId: r.id,
				record: r,
				sourceFilename: r.source_filename,
				fileId: r.file_id,
				legs: buildLegsFromRecord(r),
			});
		}
	}

	return Array.from(groupMap.values());
}

// ═══════════════════════════════════════════════════════════════════
// Editable Cell Components
// ═══════════════════════════════════════════════════════════════════

export function EditableTextCell({ recordId, field, value, editingCell, editValue, saving, onStart, onCancel, onSave, onValueChange, legSource, allFlightsIndex }: {
	recordId: string; field: string; value: string;
	editingCell: EditingCell | null;
	editValue: string; saving: boolean;
	onStart: () => void; onCancel: () => void; onSave: () => void; onValueChange: (v: string) => void;
	legSource?: EditingCell['legSource']; allFlightsIndex?: number;
}) {
	const isEditing = editingCell?.recordId === recordId && editingCell?.field === field
		&& editingCell?.legSource === legSource && editingCell?.allFlightsIndex === allFlightsIndex;
	if (isEditing) {
		return (
			<div className="flex items-center gap-1 max-w-full">
				<input type="text" value={editValue} onChange={e => onValueChange(e.target.value)}
					onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
					className="min-w-0 flex-1 px-1.5 py-0.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" autoFocus disabled={saving} />
				<button onClick={onSave} disabled={saving} className="text-green-600 hover:text-green-800 p-0.5 shrink-0">
					{saving ? <Spinner className="w-3 h-3" /> : <Check className="w-3 h-3" />}
				</button>
				<button onClick={onCancel} className="text-gray-400 hover:text-gray-600 p-0.5 shrink-0"><X className="w-3 h-3" /></button>
			</div>
		);
	}
	return (
		<span className="group inline-flex items-center gap-1 cursor-pointer" onClick={onStart}>
			<span className="text-sm">{value}</span>
			<Pencil className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
		</span>
	);
}

export function EditableNameCell({ recordId, displayName, firstName, lastName, editingCell, editValue, saving, onStart, onCancel, onSave, onValueChange, isMissingName }: {
	recordId: string; displayName: string; firstName: string; lastName: string;
	editingCell: EditingCell | null;
	editValue: string; saving: boolean;
	onStart: () => void; onCancel: () => void; onSave: () => void; onValueChange: (v: string) => void;
	isMissingName?: boolean;
}) {
	const isEditing = editingCell?.recordId === recordId && editingCell?.field === 'passenger_name_combo';
	const [first, last] = isEditing ? editValue.split('||') : [firstName, lastName];

	if (isEditing) {
		return (
			<div className="flex items-center gap-1 max-w-full">
				<input type="text" value={first || ''} placeholder="First name"
					onChange={e => onValueChange(`${e.target.value}||${last || ''}`)}
					onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
					className="min-w-0 flex-1 px-1.5 py-0.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" autoFocus disabled={saving} />
				<input type="text" value={last || ''} placeholder="Last name"
					onChange={e => onValueChange(`${first || ''}||${e.target.value}`)}
					onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
					className="min-w-0 flex-1 px-1.5 py-0.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" disabled={saving} />
				<button onClick={onSave} disabled={saving} className="text-green-600 hover:text-green-800 p-0.5 shrink-0">
					{saving ? <Spinner className="w-3 h-3" /> : <Check className="w-3 h-3" />}
				</button>
				<button onClick={onCancel} className="text-gray-400 hover:text-gray-600 p-0.5 shrink-0"><X className="w-3 h-3" /></button>
			</div>
		);
	}
	return (
		<span className="group inline-flex items-center gap-1 cursor-pointer" onClick={onStart}>
			{isMissingName ? (
				<span className="text-xs text-red-600 italic">{'\u2014'} <em>Pending name entry</em></span>
			) : (
				<span className="text-xs">{displayName}</span>
			)}
			<Pencil className={`w-3 h-3 transition-opacity ${isMissingName ? 'text-red-400 opacity-60 group-hover:opacity-100' : 'text-gray-300 opacity-0 group-hover:opacity-100'}`} />
		</span>
	);
}

export function EditableDateCell({ recordId, field, value, editingCell, editValue, saving, onStart, onCancel, onSave, onValueChange, legSource, allFlightsIndex }: {
	recordId: string; field: string; value: string | null;
	editingCell: EditingCell | null;
	editValue: string; saving: boolean;
	onStart: () => void; onCancel: () => void; onSave: () => void; onValueChange: (v: string) => void;
	legSource?: EditingCell['legSource']; allFlightsIndex?: number;
}) {
	const isEditing = editingCell?.recordId === recordId && editingCell?.field === field
		&& editingCell?.legSource === legSource && editingCell?.allFlightsIndex === allFlightsIndex;
	if (isEditing) {
		return (
			<div className="flex items-center gap-1 max-w-full">
				<input type="date" value={editValue} onChange={e => onValueChange(e.target.value)}
					onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
					className="min-w-0 flex-1 px-1 py-0.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" autoFocus disabled={saving} />
				<button onClick={onSave} disabled={saving} className="text-green-600 hover:text-green-800 p-0.5 shrink-0">
					{saving ? <Spinner className="w-3 h-3" /> : <Check className="w-3 h-3" />}
				</button>
				<button onClick={onCancel} className="text-gray-400 hover:text-gray-600 p-0.5 shrink-0"><X className="w-3 h-3" /></button>
			</div>
		);
	}
	return (
		<span className="group inline-flex items-center gap-1 cursor-pointer" onClick={onStart}>
			<span className="text-sm">{formatDate(value)}</span>
			<Pencil className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
		</span>
	);
}

export function EditableTimeCell({ recordId, field, value, editingCell, editValue, saving, onStart, onCancel, onSave, onValueChange, legSource, allFlightsIndex }: {
	recordId: string; field: string; value: string | null;
	editingCell: EditingCell | null;
	editValue: string; saving: boolean;
	onStart: () => void; onCancel: () => void; onSave: () => void; onValueChange: (v: string) => void;
	legSource?: EditingCell['legSource']; allFlightsIndex?: number;
}) {
	const isEditing = editingCell?.recordId === recordId && editingCell?.field === field
		&& editingCell?.legSource === legSource && editingCell?.allFlightsIndex === allFlightsIndex;
	if (isEditing) {
		return (
			<div className="flex items-center gap-1 max-w-full">
				<input type="time" value={editValue} onChange={e => onValueChange(e.target.value)}
					onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
					className="min-w-0 flex-1 px-1 py-0.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" autoFocus disabled={saving} />
				<button onClick={onSave} disabled={saving} className="text-green-600 hover:text-green-800 p-0.5 shrink-0">
					{saving ? <Spinner className="w-3 h-3" /> : <Check className="w-3 h-3" />}
				</button>
				<button onClick={onCancel} className="text-gray-400 hover:text-gray-600 p-0.5 shrink-0"><X className="w-3 h-3" /></button>
			</div>
		);
	}
	return (
		<span className="group inline-flex items-center gap-1 cursor-pointer" onClick={onStart}>
			<span className="text-sm">{formatTime24(value)}</span>
			<Pencil className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
		</span>
	);
}

export function EditablePNRCell({ recordId, value, editingCell, editValue, saving, onStart, onCancel, onSave, onValueChange }: {
	recordId: string; value: string;
	editingCell: EditingCell | null;
	editValue: string; saving: boolean;
	onStart: () => void; onCancel: () => void; onSave: () => void; onValueChange: (v: string) => void;
}) {
	const isEditing = editingCell?.recordId === recordId && editingCell?.field === 'primary_pnr';
	if (isEditing) {
		return (
			<div className="flex items-center gap-1 max-w-full">
				<input type="text" value={editValue}
					onChange={e => {
						const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
						onValueChange(v);
					}}
					onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
					maxLength={6}
					placeholder="ABC123"
					className="min-w-0 flex-1 px-1 py-0.5 text-xs font-mono border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase" autoFocus disabled={saving} />
				<button onClick={onSave} disabled={saving} className="text-green-600 hover:text-green-800 p-0.5 shrink-0">
					{saving ? <Spinner className="w-3 h-3" /> : <Check className="w-3 h-3" />}
				</button>
				<button onClick={onCancel} className="text-gray-400 hover:text-gray-600 p-0.5 shrink-0"><X className="w-3 h-3" /></button>
			</div>
		);
	}
	return (
		<span className="group inline-flex items-center gap-1 cursor-pointer" onClick={onStart}>
			<span className="font-mono text-sm">{value || '\u2014'}</span>
			<Pencil className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
		</span>
	);
}

export function EditableAirportRoute({ recordId, departureAirport, arrivalAirport, legSource, allFlightsIndex, editingCell, editValue, saving, manifestService, onStartEdit, onCancelEdit, onSaveEdit, onEditValueChange, airportNames }: {
	recordId: string;
	departureAirport: string | null;
	arrivalAirport: string | null;
	legSource: EditingCell['legSource'];
	allFlightsIndex?: number;
	editingCell: EditingCell | null;
	editValue: string;
	saving: boolean;
	manifestService: FlightManifestService;
	onStartEdit: (id: string, field: string, val: string, legSource?: EditingCell['legSource'], idx?: number) => void;
	onCancelEdit: () => void;
	onSaveEdit: () => void;
	onEditValueChange: (v: string) => void;
	airportNames?: Record<string, string>;
}) {
	const isEditingDep = editingCell?.recordId === recordId && editingCell?.field === 'departure_airport'
		&& editingCell?.legSource === legSource && editingCell?.allFlightsIndex === allFlightsIndex;
	const isEditingArr = editingCell?.recordId === recordId && editingCell?.field === 'arrival_airport'
		&& editingCell?.legSource === legSource && editingCell?.allFlightsIndex === allFlightsIndex;

	const handleSearch = async (query: string) => {
		const results = await manifestService.searchAirports(query);
		return results.map(a => ({ iata: a.iata, name: a.name }));
	};

	if (isEditingDep) {
		return (
			<div className="flex items-center gap-1 max-w-full">
				<AirportAutocomplete
					value={editValue}
					onChange={(iata) => { onEditValueChange(iata); }}
					onSearch={handleSearch}
					placeholder="DEP"
					className="w-24"
				/>
				<span className="text-gray-400">{'\u2192'}</span>
				<span className="font-mono text-sm text-emerald-700">{arrivalAirport || '\u2014'}</span>
				<button onClick={onSaveEdit} disabled={saving} className="text-green-600 hover:text-green-800 p-0.5 ml-1">
					{saving ? <Spinner className="w-3 h-3" /> : <Check className="w-3 h-3" />}
				</button>
				<button onClick={onCancelEdit} className="text-gray-400 hover:text-gray-600 p-0.5"><X className="w-3 h-3" /></button>
			</div>
		);
	}

	if (isEditingArr) {
		return (
			<div className="flex items-center gap-1">
				<span className="font-mono text-sm text-emerald-700">{departureAirport || '\u2014'}</span>
				<span className="text-gray-400">{'\u2192'}</span>
				<AirportAutocomplete
					value={editValue}
					onChange={(iata) => { onEditValueChange(iata); }}
					onSearch={handleSearch}
					placeholder="ARR"
					className="w-24"
				/>
				<button onClick={onSaveEdit} disabled={saving} className="text-green-600 hover:text-green-800 p-0.5 ml-1">
					{saving ? <Spinner className="w-3 h-3" /> : <Check className="w-3 h-3" />}
				</button>
				<button onClick={onCancelEdit} className="text-gray-400 hover:text-gray-600 p-0.5"><X className="w-3 h-3" /></button>
			</div>
		);
	}

	const depName = departureAirport && airportNames?.[departureAirport];
	const arrName = arrivalAirport && airportNames?.[arrivalAirport];

	const depSpan = (
		<span className="group inline-flex items-center gap-0.5 cursor-pointer"
			onClick={() => onStartEdit(recordId, 'departure_airport', departureAirport || '', legSource, allFlightsIndex)}>
			{departureAirport || '\u2014'}
			<Pencil className="w-2.5 h-2.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
		</span>
	);

	const arrSpan = (
		<span className="group inline-flex items-center gap-0.5 cursor-pointer"
			onClick={() => onStartEdit(recordId, 'arrival_airport', arrivalAirport || '', legSource, allFlightsIndex)}>
			{arrivalAirport || '\u2014'}
			<Pencil className="w-2.5 h-2.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
		</span>
	);

	return (
		<span className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-emerald-700">
			{depName ? <SimpleTooltip content={depName}>{depSpan}</SimpleTooltip> : depSpan}
			<span className="text-gray-500">{'\u2192'}</span>
			{arrName ? <SimpleTooltip content={arrName}>{arrSpan}</SimpleTooltip> : arrSpan}
		</span>
	);
}

export function SourceFileButton({ fileId, filename, fileLoading, onViewSource }: {
	fileId: string; filename: string; fileLoading: string | null; onViewSource: (fileId: string, filename: string) => void;
}) {
	return (
		<button
			className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 max-w-[160px]"
			onClick={() => onViewSource(fileId, filename)}
			disabled={fileLoading === fileId}
			title={filename}
		>
			{fileLoading === fileId ? <Spinner className="w-3 h-3" /> : <Eye className="w-3 h-3 shrink-0" />}
			<span className="truncate">{filename}</span>
		</button>
	);
}

// ═══════════════════════════════════════════════════════════════════
// Passenger Group Rows
// ═══════════════════════════════════════════════════════════════════

export function PassengerGroupRows({ group, editingCell, editValue, saving, fileLoading, manifestService, onStartEdit, onCancelEdit, onSaveEdit, onEditValueChange, onViewSource, onDeletePassenger, onDeleteBooking, onDeleteLeg, addingLeg, newLeg, onAddLeg, onCancelAddLeg, onSaveNewLeg, onNewLegChange, airportNames, selected, onSelect }: {
	group: PassengerGroup;
	editingCell: EditingCell | null;
	editValue: string;
	saving: boolean;
	fileLoading: string | null;
	manifestService: FlightManifestService;
	onStartEdit: (id: string, field: string, val: string, legSource?: EditingCell['legSource'], idx?: number) => void;
	onCancelEdit: () => void;
	onSaveEdit: () => void;
	onEditValueChange: (v: string) => void;
	onViewSource: (fileId: string, filename: string) => void;
	onDeletePassenger: (group: PassengerGroup) => void;
	onDeleteBooking: (booking: BookingGroup) => void;
	onDeleteLeg: (booking: BookingGroup, leg: FlightLeg) => void;
	addingLeg: { recordId: string } | null;
	newLeg: NewLegData;
	onAddLeg: (recordId: string) => void;
	onCancelAddLeg: () => void;
	onSaveNewLeg: () => void;
	onNewLegChange: (leg: NewLegData) => void;
	airportNames?: Record<string, string>;
	selected?: boolean;
	onSelect?: () => void;
}) {
	const isMissingName = !group.name;
	const bgTint = group.isApproved ? '' : 'bg-gray-50/60';

	const statusBadge = useMemo(() => {
		const destBadge = group.destinationMismatch ? (
			<SimpleTooltip content="Destination mismatch \u2014 no clean arrival into and departure from the program destination (e.g. open-jaw, one-way, or self-booked flights). Verify the outbound arrival / return departure airports manually, or leave blank for non-air travel.">
				<span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 cursor-help whitespace-nowrap">
					{'\u26a0'} Verify dest
				</span>
			</SimpleTooltip>
		) : null;

		const stateBadge = group.isApproved ? (
			<SimpleTooltip content="Confirmed">
				<span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-600 cursor-help">{'\u2713'}</span>
			</SimpleTooltip>
		) : (
			<SimpleTooltip content="Pending review">
				<span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-50 text-gray-400 cursor-help">{'\u2014'}</span>
			</SimpleTooltip>
		);

		if (!destBadge) return stateBadge;
		return (
			<div className="flex flex-col items-center gap-1">
				{destBadge}
				{stateBadge}
			</div>
		);
	}, [group.anomalyTypes, group.isApproved, group.destinationMismatch]);

	const rows: React.ReactNode[] = [];
	const totalLegs = group.bookings.reduce((sum, b) => sum + b.legs.length, 0);
	const hasSingleBookingSingleLeg = group.bookings.length === 1 && group.bookings[0].legs.length <= 1;

	// Helper to render add-leg form row
	const addLegFormRow = (recordId: string) => (
		<tr key={`add-leg-form-${recordId}`} className="border-b bg-blue-50/40">
			{onSelect !== undefined && <td className="px-2 py-1.5" />}
			<td className="px-2 py-1.5" />
			<td className="px-2 py-1.5 pl-10">
				<select value={newLeg.direction} onChange={e => onNewLegChange({ ...newLeg, direction: e.target.value as 'outbound' | 'return' })}
					className="text-[10px] border rounded px-1.5 py-0.5 bg-white">
					<option value="outbound">{'\u2192'} Outbound</option>
					<option value="return">{'\u2190'} Return</option>
				</select>
			</td>
			<td className="px-2 py-1.5">
				<div className="flex items-center gap-1">
					<input type="text" placeholder="DEP" value={newLeg.departure_airport} onChange={e => onNewLegChange({ ...newLeg, departure_airport: e.target.value.toUpperCase() })}
						className="w-12 text-xs font-mono border rounded px-1.5 py-0.5 uppercase" maxLength={3} />
					<span className="text-gray-400 text-xs">{'\u2192'}</span>
					<input type="text" placeholder="ARR" value={newLeg.arrival_airport} onChange={e => onNewLegChange({ ...newLeg, arrival_airport: e.target.value.toUpperCase() })}
						className="w-12 text-xs font-mono border rounded px-1.5 py-0.5 uppercase" maxLength={3} />
				</div>
			</td>
			<td className="px-2 py-1.5">
				<input type="text" placeholder="DL123" value={newLeg.flight_number} onChange={e => onNewLegChange({ ...newLeg, flight_number: e.target.value })}
					className="w-16 text-xs font-mono border rounded px-1.5 py-0.5" />
			</td>
			<td className="px-2 py-1.5">
				<input type="date" value={newLeg.date} onChange={e => onNewLegChange({ ...newLeg, date: e.target.value })}
					className="text-xs border rounded px-1.5 py-0.5 w-[120px]" />
			</td>
			<td className="px-2 py-1.5">
				<input type="time" value={newLeg.departure_time} onChange={e => onNewLegChange({ ...newLeg, departure_time: e.target.value })}
					className="text-xs border rounded px-1.5 py-0.5 w-[80px]" />
			</td>
			<td className="px-2 py-1.5">
				<input type="time" value={newLeg.arrival_time} onChange={e => onNewLegChange({ ...newLeg, arrival_time: e.target.value })}
					className="text-xs border rounded px-1.5 py-0.5 w-[80px]" />
			</td>
			<td className="px-2 py-1.5" colSpan={3}>
				<div className="flex items-center gap-1">
					<button onClick={onSaveNewLeg} disabled={saving || !newLeg.departure_airport || !newLeg.arrival_airport || !newLeg.date}
						className="text-xs px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
						{saving ? '...' : 'Save'}
					</button>
					<button onClick={onCancelAddLeg} className="text-xs px-2 py-0.5 rounded border text-gray-500 hover:bg-gray-100">Cancel</button>
				</div>
			</td>
		</tr>
	);

	const addLegButtonRow = (recordId: string) => (
		<tr key={`add-leg-btn-${recordId}`} className="border-b border-gray-50">
			{onSelect !== undefined && <td className="px-2 py-0" />}
			<td className="px-2 py-0" />
			<td className="px-2 py-0.5 pl-10" colSpan={9}>
				<button onClick={() => onAddLeg(recordId)} className="text-[10px] text-blue-500 hover:text-blue-700 font-medium flex items-center gap-0.5 py-0.5">
					<Plus className="w-3 h-3" /> Add leg
				</button>
			</td>
		</tr>
	);

	if (hasSingleBookingSingleLeg) {
		const booking = group.bookings[0];
		const leg = booking.legs[0];
		const r = booking.record;

		rows.push(
			<tr key={group.key} className={`group border-b hover:bg-gray-100/50 transition-colors ${bgTint} ${selected ? 'bg-primary-50/40' : ''}`}>
				{onSelect !== undefined && (
					<td className="p-2 text-center" rowSpan={1}>
						<input
							type="checkbox"
							checked={!!selected}
							onChange={onSelect}
							className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
							aria-label={`Select ${group.name || 'passenger'}`}
						/>
					</td>
				)}
				<td className="p-3 text-center">{statusBadge}</td>
				<td className="p-3 whitespace-nowrap overflow-hidden font-semibold">
					<EditableNameCell
						recordId={booking.recordId}
						displayName={group.name || '\u2014'}
						firstName={r.passenger_first_name || ''}
						lastName={r.passenger_last_name || ''}
						editingCell={editingCell} editValue={editValue} saving={saving}
						onStart={() => onStartEdit(booking.recordId, 'passenger_name_combo', `${r.passenger_first_name || ''}||${r.passenger_last_name || ''}`)}
						onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
						isMissingName={isMissingName}
					/>
					{group.email && <div className="text-[11px] text-gray-400 font-normal">{group.email}</div>}
				</td>
				<td className="p-3 whitespace-nowrap overflow-hidden">
					{leg ? (
						<EditableAirportRoute
							recordId={booking.recordId}
							departureAirport={leg.departureAirport}
							arrivalAirport={leg.arrivalAirport}
							legSource={leg.source}
							allFlightsIndex={leg.allFlightsIndex}
							editingCell={editingCell}
							editValue={editValue}
							saving={saving}
							manifestService={manifestService}
							onStartEdit={onStartEdit}
							onCancelEdit={onCancelEdit}
							onSaveEdit={onSaveEdit}
							onEditValueChange={onEditValueChange}
							airportNames={airportNames}
						/>
					) : '\u2014'}
				</td>
				<td className="p-3 whitespace-nowrap overflow-hidden">
					{leg ? (
						<EditableTextCell
							recordId={booking.recordId} field="flight_number"
							value={leg.flightNumber || '\u2014'}
							editingCell={editingCell} editValue={editValue} saving={saving}
							onStart={() => onStartEdit(booking.recordId, 'flight_number', leg.flightNumber || '', leg.source, leg.allFlightsIndex)}
							onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
							legSource={leg.source} allFlightsIndex={leg.allFlightsIndex}
						/>
					) : '\u2014'}
				</td>
				<td className="p-3 whitespace-nowrap overflow-hidden text-gray-600">
					{leg ? (
						<EditableDateCell
							recordId={booking.recordId} field="date"
							value={leg.date}
							editingCell={editingCell} editValue={editValue} saving={saving}
							onStart={() => onStartEdit(booking.recordId, 'date', leg.date || '', leg.source, leg.allFlightsIndex)}
							onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
							legSource={leg.source} allFlightsIndex={leg.allFlightsIndex}
						/>
					) : '\u2014'}
				</td>
				<td className="p-3 whitespace-nowrap overflow-hidden text-gray-600">
					{leg ? (
						<EditableTimeCell
							recordId={booking.recordId} field="departure_time"
							value={leg.departureTime}
							editingCell={editingCell} editValue={editValue} saving={saving}
							onStart={() => onStartEdit(booking.recordId, 'departure_time', leg.departureTime || '', leg.source, leg.allFlightsIndex)}
							onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
							legSource={leg.source} allFlightsIndex={leg.allFlightsIndex}
						/>
					) : '\u2014'}
				</td>
				<td className="p-3 whitespace-nowrap overflow-hidden text-gray-600">
					{leg ? (
						<EditableTimeCell
							recordId={booking.recordId} field="arrival_time"
							value={leg.arrivalTime}
							editingCell={editingCell} editValue={editValue} saving={saving}
							onStart={() => onStartEdit(booking.recordId, 'arrival_time', leg.arrivalTime || '', leg.source, leg.allFlightsIndex)}
							onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
							legSource={leg.source} allFlightsIndex={leg.allFlightsIndex}
						/>
					) : '\u2014'}
				</td>
				<td className="p-3 whitespace-nowrap overflow-hidden">
					<EditablePNRCell
						recordId={booking.recordId}
						value={booking.pnr.startsWith('record-') ? '' : booking.pnr}
						editingCell={editingCell} editValue={editValue} saving={saving}
						onStart={() => onStartEdit(booking.recordId, 'primary_pnr', r.primary_pnr || '')}
						onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
					/>
				</td>
				<td className="p-3 whitespace-nowrap overflow-hidden">
					<SourceFileButton fileId={booking.fileId} filename={booking.sourceFilename} fileLoading={fileLoading} onViewSource={onViewSource} />
				</td>
				<td className="p-1 text-center">
					<button onClick={() => onDeletePassenger(group)} className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-gray-300 hover:text-red-500" title="Delete passenger">
						<Trash2 className="w-3.5 h-3.5" />
					</button>
				</td>
			</tr>
		);
		// Add Leg button / form for compact view
		const compactRecordId = booking.recordId;
		if (addingLeg?.recordId === compactRecordId) {
			rows.push(addLegFormRow(compactRecordId));
		} else {
			rows.push(addLegButtonRow(compactRecordId));
		}
	} else {
		// Multi-booking or multi-leg: passenger header row + booking sub-rows + leg rows
		rows.push(
			<tr key={`${group.key}-header`} className={`group border-b ${bgTint} ${selected ? 'bg-primary-50/40' : ''}`}>
				{onSelect !== undefined && (
					<td className="p-2 text-center align-top">
						<input
							type="checkbox"
							checked={!!selected}
							onChange={onSelect}
							className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
							aria-label={`Select ${group.name || 'passenger'}`}
						/>
					</td>
				)}
				<td className="p-3 text-center align-top">{statusBadge}</td>
				<td className="p-3 whitespace-nowrap font-semibold align-top" colSpan={8}>
					<div className="flex items-center gap-3">
						<EditableNameCell
							recordId={group.bookings[0].recordId}
							displayName={group.name || '\u2014'}
							firstName={group.bookings[0].record.passenger_first_name || ''}
							lastName={group.bookings[0].record.passenger_last_name || ''}
							editingCell={editingCell} editValue={editValue} saving={saving}
							onStart={() => onStartEdit(group.bookings[0].recordId, 'passenger_name_combo', `${group.bookings[0].record.passenger_first_name || ''}||${group.bookings[0].record.passenger_last_name || ''}`)}
							onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
							isMissingName={isMissingName}
						/>
						{group.email && <span className="text-[11px] text-gray-400 font-normal">{group.email}</span>}
						<span className="text-[10px] text-gray-400 font-normal ml-auto">
							{group.bookings.length} booking{group.bookings.length !== 1 ? 's' : ''}, {totalLegs} leg{totalLegs !== 1 ? 's' : ''}
						</span>
					</div>
				</td>
				<td className="p-1 text-center align-middle">
					<button onClick={() => onDeletePassenger(group)} className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-gray-300 hover:text-red-500" title="Delete passenger">
						<Trash2 className="w-3.5 h-3.5" />
					</button>
				</td>
			</tr>
		);

		for (const booking of group.bookings) {
			const r = booking.record;
			rows.push(
				<tr key={`booking-${booking.recordId}`} className={`group/booking border-b border-gray-100 ${bgTint}`}>
					{onSelect !== undefined && <td className="px-2 py-0.5" />}
					<td className="px-2 py-0.5" />
					<td className="px-2 py-0.5 pl-6" colSpan={6}>
						<span className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Booking: </span>
						<EditablePNRCell
							recordId={booking.recordId}
							value={booking.pnr.startsWith('record-') ? '' : booking.pnr}
							editingCell={editingCell} editValue={editValue} saving={saving}
							onStart={() => onStartEdit(booking.recordId, 'primary_pnr', r.primary_pnr || '')}
							onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
						/>
					</td>
					<td className="px-2 py-0.5" />
					<td className="px-2 py-0.5">
						<SourceFileButton fileId={booking.fileId} filename={booking.sourceFilename} fileLoading={fileLoading} onViewSource={onViewSource} />
					</td>
					<td className="px-1 py-0.5 text-center">
						<button onClick={() => onDeleteBooking(booking)} className="opacity-0 group-hover/booking:opacity-100 transition-opacity p-0.5 text-gray-300 hover:text-red-500" title="Delete booking">
							<Trash2 className="w-3 h-3" />
						</button>
					</td>
				</tr>
			);

			for (const leg of booking.legs) {
				const dirLabel = leg.direction === 'outbound' ? '\u2192' : '\u2190';
				rows.push(
					<tr key={`leg-${booking.recordId}-${leg.source}-${leg.allFlightsIndex ?? leg.direction}`} className={`group/leg border-b border-gray-50 hover:bg-gray-50/50 ${bgTint}`}>
						{onSelect !== undefined && <td className="px-2 py-0" />}
						<td className="px-2 py-0" />
						<td className="px-2 py-0 pl-10">
							<span className="text-xs uppercase tracking-wider text-gray-600 font-semibold">{dirLabel} {leg.direction}</span>
						</td>
						<td className="px-2 py-0 whitespace-nowrap overflow-hidden">
							<EditableAirportRoute
								recordId={booking.recordId}
								departureAirport={leg.departureAirport}
								arrivalAirport={leg.arrivalAirport}
								legSource={leg.source}
								allFlightsIndex={leg.allFlightsIndex}
								editingCell={editingCell}
								editValue={editValue}
								saving={saving}
								manifestService={manifestService}
								onStartEdit={onStartEdit}
								onCancelEdit={onCancelEdit}
								onSaveEdit={onSaveEdit}
								onEditValueChange={onEditValueChange}
							/>
						</td>
						<td className="px-2 py-0 whitespace-nowrap overflow-hidden">
							<EditableTextCell
								recordId={booking.recordId} field="flight_number"
								value={leg.flightNumber || '\u2014'}
								editingCell={editingCell} editValue={editValue} saving={saving}
								onStart={() => onStartEdit(booking.recordId, 'flight_number', leg.flightNumber || '', leg.source, leg.allFlightsIndex)}
								onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
								legSource={leg.source} allFlightsIndex={leg.allFlightsIndex}
							/>
						</td>
						<td className="px-2 py-0 whitespace-nowrap overflow-hidden text-gray-600">
							<EditableDateCell
								recordId={booking.recordId} field="date"
								value={leg.date}
								editingCell={editingCell} editValue={editValue} saving={saving}
								onStart={() => onStartEdit(booking.recordId, 'date', leg.date || '', leg.source, leg.allFlightsIndex)}
								onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
								legSource={leg.source} allFlightsIndex={leg.allFlightsIndex}
							/>
						</td>
						<td className="px-2 py-0 whitespace-nowrap overflow-hidden text-gray-600">
							<EditableTimeCell
								recordId={booking.recordId} field="departure_time"
								value={leg.departureTime}
								editingCell={editingCell} editValue={editValue} saving={saving}
								onStart={() => onStartEdit(booking.recordId, 'departure_time', leg.departureTime || '', leg.source, leg.allFlightsIndex)}
								onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
								legSource={leg.source} allFlightsIndex={leg.allFlightsIndex}
							/>
						</td>
						<td className="px-2 py-0 whitespace-nowrap overflow-hidden text-gray-600">
							<EditableTimeCell
								recordId={booking.recordId} field="arrival_time"
								value={leg.arrivalTime}
								editingCell={editingCell} editValue={editValue} saving={saving}
								onStart={() => onStartEdit(booking.recordId, 'arrival_time', leg.arrivalTime || '', leg.source, leg.allFlightsIndex)}
								onCancel={onCancelEdit} onSave={onSaveEdit} onValueChange={onEditValueChange}
								legSource={leg.source} allFlightsIndex={leg.allFlightsIndex}
							/>
						</td>
						<td className="px-2 py-0" />
						<td className="px-2 py-0" />
						<td className="px-1 py-0 text-center">
							<button onClick={() => onDeleteLeg(booking, leg)} className="opacity-0 group-hover/leg:opacity-100 transition-opacity p-0.5 text-gray-300 hover:text-red-500" title="Delete leg">
								<Trash2 className="w-3 h-3" />
							</button>
						</td>
					</tr>
				);
			}
			// Add Leg button / form after legs
			if (addingLeg?.recordId === booking.recordId) {
				rows.push(addLegFormRow(booking.recordId));
			} else {
				rows.push(addLegButtonRow(booking.recordId));
			}
		}
	}

	return <>{rows}</>;
}
